/**
 * Hero
 * ====
 * Homepage hero: full-bleed cinematic Slideshow with the address/status/
 * CTAs woven into the same bottom-left composition as the slide copy.
 * PageHero: the same Slideshow component with a single slide — shorter,
 * for interior pages — so the whole site shares one motion language.
 */

import { Link } from 'react-router-dom';
import { EXTERNAL_URLS, BUSINESS_INFO } from '../config/constants';
import OpenStatus from './OpenStatus';
import Button from './ui/Button';
import Slideshow from './ui/Slideshow';
import { isUKBankHoliday } from '../utils/bankHolidays';

/**
 * Small "‹ Back to Home" link used at the top of every interior-page hero
 * — the homepage itself never renders this (it uses Hero, not PageHero).
 * Exported for the one page (Contact) that builds its own custom hero
 * instead of using PageHero.
 *
 * The top clearance below the fixed nav lives here, not at call sites —
 * Contact's bespoke hero previously forgot to add it, which is exactly
 * how this ended up sitting flush against the navbar. Nav is h-20 (80px)
 * up to lg, h-24 (96px) from lg; each tier adds ~32px of breathing room
 * on top, plus env(safe-area-inset-top) for notch/Dynamic Island devices
 * (resolves to 0 everywhere else, so it's a no-op off notched hardware).
 */
export const BackToHome = ({ className = '' }) => (
  <Link
    to="/"
    aria-label="Back to Home"
    className={`group inline-flex items-center gap-2 pt-[calc(7rem+env(safe-area-inset-top))] lg:pt-[calc(8rem+env(safe-area-inset-top))] font-sans text-sm text-cream-dim transition-colors duration-300 ease-out hover:text-amber-bright ${className}`}
  >
    <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
    <span className="link-underline">Back to Home</span>
  </Link>
);

const HOME_SLIDES = [
  {
    image: '/images/hero/hero-bg.jpg',
    alt: 'A spread of Gup Shup breakfast dishes, karak chai and street snacks on a wooden table',
    eyebrow: 'Collier Row, Romford',
    heading: <>Chit-chat,<br /><em className="italic text-amber-bright">over chai.</em></>,
    sub: 'Karak chai brewed the traditional way, and street food from Lahore to Mumbai — unhurried, the way it should be.'
  },
  {
    image: '/images/popular-items/chaat-platter.jpeg',
    alt: 'Chaat platter with Gol Gappe, Dahi Puri, Bhel Puri and Papri Chaat',
    eyebrow: 'Since January 2023',
    heading: <>Gol Gappe.<br /><em className="italic text-amber-bright">Bhel Puri.</em></>,
    sub: 'Street snacks made to share, the way markets from Lahore to Mumbai do it.'
  },
  {
    image: '/images/branding/storefront.jpg',
    alt: 'Gup Shup storefront on Clockhouse Lane, Collier Row, lit up at dusk',
    focal: 'center 30%',
    eyebrow: '3 Clockhouse Lane',
    heading: <>Find us after<br /><em className="italic text-amber-bright">the neon comes on.</em></>,
    sub: 'A warm, pendant-lit room built for long conversations.'
  },
  {
    image: '/images/popular-items/samosa-chaat.jpeg',
    alt: 'Samosa chaat topped with tamarind, sev and pomegranate',
    eyebrow: 'Made fresh, daily',
    heading: <>Every plate,<br /><em className="italic text-amber-bright">properly spiced.</em></>,
    sub: 'Traditional recipes, no shortcuts — from our kitchen to your table.'
  }
];

const Hero = () => {
  return (
    <section className="relative -mt-20 lg:-mt-24" aria-label="Gup Shup — hero">
      <Slideshow
        slides={HOME_SLIDES}
        interval={7500}
        className="h-[100svh] min-h-[600px] max-h-[980px]"
        ariaLabel="Gup Shup photography"
        renderContent={(slide) => (
          <div className="container-custom pb-14 sm:pb-16 lg:pb-20">
            <div className="max-w-3xl">
              <p className="eyebrow text-shadow-body mb-4 pt-[calc(7rem+env(safe-area-inset-top))] lg:pt-[calc(8rem+env(safe-area-inset-top))]">{slide.eyebrow}</p>
              <h1 className="font-medium text-4xl sm:text-6xl lg:text-7xl text-cream text-shadow-soft mb-6">{slide.heading}</h1>
              <p className="text-cream-dim text-shadow-body leading-relaxed text-base sm:text-lg font-sans max-w-lg mb-9">{slide.sub}</p>

              <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mb-10">
                <Button to="/order-online" variant="primary" arrow>Order Online</Button>
                <Button href={EXTERNAL_URLS.reservation} variant="ghost" className="shadow-[0_4px_20px_-4px_rgba(0,0,0,0.5)]">Book a Table</Button>
              </div>

              <div className="flex flex-col gap-2 text-shadow-body">
                {isUKBankHoliday(new Date()) && (
                  <p className="text-amber-bright text-sm font-sans font-medium">Bank Holiday — open today 9am–10pm</p>
                )}
                <OpenStatus />
                <p className="text-cream-faint text-sm font-sans">{BUSINESS_INFO.address.full}</p>
              </div>
            </div>
          </div>
        )}
      />

      {/* Scroll cue — the left column carries the eyebrow/heading/CTAs/
          address stack all the way from just under the nav to the bottom
          padding, so there's no vertical gap there safe for an overlay at
          any hero height down to min-h-[600px]. The right edge only ever
          holds the slide-indicator dots (bottom-right, ~36px tall), so the
          cue sits stacked above them instead, sharing their right offset.
          Real vertical text via writing-mode, not a rotated horizontal box
          — rotation hacks report their own (wrong) layout size, which is
          what made this clip/cram before. */}
      <div
        className="hidden lg:flex absolute right-12 bottom-24 z-20 flex-col items-center gap-4 text-cream-faint pointer-events-none"
        aria-hidden="true"
      >
        <span className="text-2xs uppercase tracking-widest2 [writing-mode:vertical-rl] [text-orientation:upright]">
          Scroll
        </span>
        <span className="relative block h-10 w-px overflow-hidden bg-cream-faint/40">
          <span className="absolute inset-x-0 top-0 h-1/2 bg-amber-bright animate-scroll-cue" />
        </span>
      </div>
    </section>
  );
};

/**
 * Page Hero - shorter, single-slide hero for interior pages.
 * Reuses Slideshow so the same slow Ken Burns drift plays once on load.
 */
export const PageHero = ({ title, subtitle, backgroundImage, focal }) => {
  return (
    <section className="relative -mt-20 lg:-mt-24" aria-label={typeof title === 'string' ? title : 'Page hero'}>
      <Slideshow
        slides={[{ image: backgroundImage, alt: '', focal }]}
        interval={9000}
        autoPlay={false}
        className="h-[46vh] min-h-[340px] lg:h-[54vh] lg:min-h-[420px]"
        renderContent={() => (
          <div className="container-custom pb-12 lg:pb-16">
            <BackToHome className="mb-5 lg:mb-6" />
            <p className="eyebrow text-shadow-body mb-3">Gup Shup — Chit Chat Chai</p>
            <h1 className="font-medium text-4xl sm:text-5xl lg:text-6xl text-cream text-shadow-soft max-w-2xl">{title}</h1>
            {subtitle && <p className="text-cream-dim text-shadow-body font-sans leading-relaxed text-base sm:text-lg mt-4 max-w-xl">{subtitle}</p>}
          </div>
        )}
      />
    </section>
  );
};

export default Hero;
